import React, {ReactNode, useEffect, useState} from "react"
import {appRoute} from "../utils/routes";
import {Box, Button, Card, Chip, Divider, Grid, IconButton, Input, LinearProgress, Stack, Typography} from "@mui/joy";
import {tests} from "../values/mockTests";
import TestItemComponent from "../components/Test/TestItemComponent";
import {resources} from "../values/mockResources";
import ResourceItemComponent from "../components/Resource/ResourceItemComponent";
import PracticeTestItemComponent from "../components/Test/PracticeTestItemComponent";
import PracticeResourceItemComponent from "../components/Resource/PracticeResourceItemComponent";
import {
    BookAudioIcon, BookOpenText,
    BrainIcon,
    CheckCheck,
    ClipboardCheck, Ear, Edit3,
    Eye,
    GitBranch,
    LayoutList,
    Menu,
    Mic,
    PenLine,
    Search,
    Type,
    Wind
} from "lucide-react";
import {resourceTypeIconMap} from "../models/resource";
import {Category} from "../models/category";
import {useAsync} from "../hooks/useAsyncResult";
import {getMockTests, getPracticeTests, getTestStats} from "../services/api/test";
import LoadingPageComponent from "../components/Loading/LoadingPageComponent";
import {SectionName} from "../models/section";
import TestAttemptComponent from "../components/Test/TestAttemptComponent";
import {Link, useSearchParams} from "react-router-dom";
import PaginationComponent from "../components/PaginationComponent";
import {usePagination} from "../hooks/usePagination";
import {useTranslation} from "react-i18next";
import PricingDialog from "../components/PricingDialog";
import {useSubscription} from "../hooks/useSubscription";
import LoadingError from "../components/Error/LoadingError";

const PracticePage = () => {
    const testStatsResultAsync = useAsync(getTestStats, true)
    const [searchParams] = useSearchParams()
    const {t} = useTranslation()

    const sectionLabelMap: { [key in SectionName]?: string } = {
        writing: "writingSectionLabel",
        speaking: "speakingSectionLabel",
        reading: "readingSectionLabel",
        listening: "listeningSectionLabel"
    }

    const testCategoryIconMapper: { [key in SectionName]?: ReactNode } = {
        listening: <Ear style={{color: "inherit"}}/>,
        writing: <Edit3 style={{color: "inherit"}}/>,
        speaking: <Mic style={{color: "inherit"}}/>,
        reading: <BookOpenText style={{color: "inherit"}}/>,
    }

    const loadTestStats = () => {
        testStatsResultAsync.execute(parseInt(searchParams.get("page") ?? "1"))

    }

    useEffect(() => {
        loadTestStats()
    }, [searchParams])

    return <Stack gap={4} padding={3}>

        <Stack>
            <Typography level={"h1"}>
                {t("practiceTitle")}
            </Typography>
            <Typography>
                {t("practiceDescription")}
            </Typography>
        </Stack>

        {!testStatsResultAsync.isSuccess && !testStatsResultAsync.loading &&
            <LoadingError label={t("practiceTitle")} onRetry={loadTestStats}/>}

        {testStatsResultAsync?.loading ?
            <LoadingPageComponent label={t("practiceTestLabel")}/> :
            testStatsResultAsync.isSuccess &&
            <Stack gap={2}>
                <Grid container spacing={2}>
                    {Object.entries(testCategoryIconMapper).map((entry, index) =>
                        <Grid xs={12} sm={6}>

                            <Link to={appRoute(`practice/${entry[0]}`)} style={{textDecoration: "none"}}>
                                <Card sx={{padding: 3}}>
                                    <Stack gap={2}>
                                        <Box data-accent width={"max-content"} display={"flex"}
                                             alignItems="center"
                                             padding={1} height={"max-content"}
                                             sx={{
                                                 bgcolor: "primary.softBg",
                                                 color: "primary.softColor"
                                             }} borderRadius={10}>
                                            {entry[1]}
                                        </Box>

                                        <Stack gap={1}>
                                            <Stack>
                                                <Typography level={"title-lg"} sx={{textTransform: 'capitalize'}}>
                                                    {t(sectionLabelMap[entry[0]])}
                                                </Typography>
                                                <Typography>
                                                    {testStatsResultAsync.payload?.[entry[0] as SectionName]?.total} Tests
                                                </Typography>
                                            </Stack>

                                            <Stack gap={0.5}>
                                                <Stack direction={"row"} alignItems={"center"}
                                                       justifyContent={"space-between"}>
                                                    <Typography>
                                                        {t("progressLabel")}
                                                    </Typography>

                                                    <Typography>
                                                        {testStatsResultAsync.payload?.[entry[0] as SectionName]?.attempts || 0}/{testStatsResultAsync.payload?.[entry[0] as SectionName]?.total}
                                                    </Typography>
                                                </Stack>

                                                <LinearProgress determinate
                                                                value={(testStatsResultAsync.payload?.[entry[0] as SectionName]?.attempts || 0) / (testStatsResultAsync.payload?.[entry[0] as SectionName]?.total) * 100}
/>

                                            </Stack>
                                        </Stack>

                                    </Stack>
                                </Card>
                            </Link>


                        </Grid>)}
                </Grid>

            </Stack>}

    </Stack>
}

export default PracticePage